// Uppy integration
import Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import GoogleDrive from "@uppy/google-drive";
import Dropbox from "@uppy/dropbox";
// import OneDrive from "@uppy/onedrive";
import Url from "@uppy/url";
import Transloadit from "@uppy/transloadit";

import { toast } from 'react-toastify';
import moment from 'moment';
import { addEventNameToEndpoint } from '../../utils/common';
import _ from "lodash";

// APIs
import uuidv4 from "uuid/v4";
import APIUrls from "../../constants/ApiUrls";
import { APIManager } from "../../utils/api";
import { GooglePickerPlugin } from "./pickers/GooglePickerPlugin";

const NOTIFY_URL = `${process.env.REACT_APP_TRANSLOADIT_NOTIFY_URL_UPLOAD}/items/v1/notifications`;
const CC_COMPANION_URL = process.env.REACT_APP_COMPANION_URL; // instead of Transloadit.COMPANION;

const IMPORT_STATUS = {
  INPROCESS: "INPROCESS",
  COMPLETED: "COMPLETED",
  DELETED: "DELETED",
  DELETE_FAIL: "DELETE FAIL"
}

// ////////////////////////////////////////////////////////////////
// Uppy Helper methods

/**
 * Complete the URL upload using the a few of the Uppy init options and props
 * Auth - the Auth object from AWS Amplify
 * cognitoUser - the cognitoUser object from AWS Amplify
 * urlFiles - the files to import
 * @param {Object} options We use the Uppy initialization options here
 */
export const importUrlFiles = async (options) => {
  // console.log({ options, getUrlFiles: options.getUrlFiles, info: "importUrlFiles" });
  try {
    const { tenantId, userId, itemId, Auth, getUrlFiles } = options;
    const importId = uuidv4();
    // get updated session
    const tokens = await Auth.currentSession();
    const axiosConfig = APIManager.Instance.getAuthorizedRequestConfig(tokens);
    
    // get the links to import
    const urlFiles = getUrlFiles();
    if (!urlFiles || urlFiles.length === 0) {
      console.error("No URL links to import");
      return;
    }
    // console.log({ urlFiles, info: "importUrlFiles" });

    const assemblyFields = addCollectionFieldsIfNecessary({userId}, options.collectionState);
    const historyData = {
      importId,
      tenantId,
      itemId,
      userId,
      filename: `url-import-${importId}`,
      status: IMPORT_STATUS.INPROCESS,
      files: urlFiles.map((file) => {
        file.meta.source =
          (file.meta && file.meta.url) ||
          (file.remote && file.remote.body && file.remote.body.url) ||
          (file.remote && file.remote.body && file.remote.body.fileId);
        file.meta.importId = importId;
        file.meta.itemId = itemId;
        return {
          source: file.source,
          id: file.id,
          tenantId: tenantId,
          meta: file.meta,
          type: file.type,
          userId: userId,
          ...assemblyFields, // collection info
          size: file.size,
        };
      }),
    };

    if (options.accessToken) {
      historyData.tokens = { [options.tokenSource]: options.accessToken };
    }
    // create import history for URLs
    let result = await APIManager.Instance.call("POST", APIUrls.getImportHistory, axiosConfig, historyData);
    // then complete import
    result = await APIManager.Instance.call("PUT", APIUrls.updateImportHistoryStatus, axiosConfig, { status: IMPORT_STATUS.COMPLETED, importId });
  } catch (error) {
    console.error(error);
  }
};

// the form fields for a non-update (regular) add case. This covers
// the content title and whether it's internally or eternally shared
export const getMetaFields = (isUpdate = false) => {
  let metaFields = [];
  if (!isUpdate) {
    metaFields = [
      { id: "contentTitle", name: "Title", placeholder: "Asset title" },
      {
        id: "isShared",
        name: "Internal Only", // we message this differently to users
        render({ value, onChange, required, form }, h) {
          // console.log({ value, onChange, required, form, state: this.state });
          return h("input", {
            type: "checkbox",
            required,
            form,
            onChange: (ev) => onChange(ev.target.checked ? "false" : "true"), // values opposite of "isShared"
            defaultChecked: value === "false",
          });
        },
      },
    ];
  }
  // the Update form allows for reprocessing of the thumbnail
  if (isUpdate) {
    metaFields = [
      {
        id: "updateThumbnail",
        name: "Update Thumbnail", // we message this differently to users
        render({ value, onChange, required, form }, h) {
          // console.log({ value, onChange, required, form, state: this.state });
          return h("input", {
            type: "checkbox",
            required,
            form,
            onChange: (ev) => onChange(ev.target.checked ? "true" : "false"),
            defaultChecked: value === "true", // so we don't wipe out thumbs that have already been customized
          });
        },
      },
    ];
  }
  return metaFields;
};

export const addCollectionFieldsIfNecessary = (assemblyFields, collectionState) => {
  if (collectionState && collectionState.value === "collectionEditing") {
    assemblyFields.ownerId = assemblyFields.userId;
    assemblyFields.isGloballyVisible = false;
    assemblyFields.collectionId = collectionState.event.collection.id;
  }
  return assemblyFields;
};

export const handleBeforeUpload = (files, getUrlFiles, setUrlFiles) => {
  // console.log({ files, getUrlFiles, setUrlFiles, info: "handleBeforeUpload" });
  const filesToUpload = {};
  for (const [key, file] of Object.entries(files)) {
    // if this is a link to a page and not a direct link to an asset
    if (file && file.type && (file.type.startsWith("text/html") || file.source==='GoogleDrive')) {
      // don't add the file - handle it as URLs
      const urlFiles = [...getUrlFiles(), file];
      // console.log({ urlFiles, info: "handleBeforeUpload" });
      setUrlFiles(urlFiles);
    } else {
      filesToUpload[key] = files[key];
    }
  }
  return filesToUpload;
};

// ////////////////////////////////////////////////
// Uppy initialization

/**
 * Initialize Uppy for different uses like uploading from modal, uploading during onboarding, uploading the avatar, and uploading branding images
 * options is of the form: 
 * {
 * id: 'uppyChangeThumbnailModal',
 * target: '.uppy-thumbnail-container',
 * inline: true,
 * tenantId,
 * userId,
 * tokens: { accessToken, idToken },
 * itemId, // for update, optional
 * debug: false,
 * autoProceed: false,
 * restrictions: {}
 * meta: {},
 * onBeforeUpload: () => {},
 * cognitoUser, // prop
 * Analytics, // prop
 * Auth // prop
 * items // prop
 * collectionState // prop
 * path // prop
 * onClose // prop
 * }
 * @param {Object} options 
 * @returns 
 */
export const initializeUppy = (options) => {
  // console.log({ options, info: "initializeUppy" });
  const uppy = new Uppy({
    id: options.id || "uppy",
    debug: options.debug || false,
    autoProceed: options.autoProceed || false,
    restrictions: options.restrictions || {
      minNumberOfFiles: 0,
      maxNumberOfFiles: options.itemId ? 1 : 0, // update only allows for 1 file
    },
    meta: options.meta || {
      source: "",
      tenantId: options.tenantId,
    },
    onBeforeUpload: (files) => {
      if (options.onBeforeUpload) {
        return options.onBeforeUpload(files);
      } else {
        // allow the urlFiles state to be updated on the parent component
        return handleBeforeUpload(files, options.getUrlFiles, options.setUrlFiles);
      }
    },
    onBeforeFileAdded: (file) => {
      // console.log({ file, info: "uppy.onBeforeFileAdded" });
      if (file.source === "GoogleDrive") {
        file.id = file.meta.originalId;
        file.meta.name = file.name;
      }
      return file;
    },
  }).use(Dashboard, {
      trigger: ".UppyModalOpenerBtn",
      inline: options.inline || true,
      target: options.target || ".uppy-container",
      replaceTargetContent: true,
      showProgressDetails: true,
      height: 470,
      browserBackButtonClose: true,
      proudlyDisplayPoweredByUppy: false,
      waitForMetadata: true, // https://uppy.io/docs/transloadit/#waitForMetadata
      metaFields: getMetaFields(!!options.itemId), // we have an update to an existing item
    })
    // .use(GoogleDrive, { target: Dashboard, companionUrl: CC_COMPANION_URL })
    .use(GooglePickerPlugin, { options  }) // new google drive picker
    .use(Dropbox, { target: Dashboard, companionUrl: CC_COMPANION_URL })
    // .use(OneDrive, { target: Dashboard, companionUrl: CC_COMPANION_URL })
    .use(Url, { target: Dashboard, companionUrl: CC_COMPANION_URL })
    .use(Transloadit, {
      waitForMetadata: false,
      getAssemblyOptions: (file) => {
        let templateId = process.env.REACT_APP_TEMPLATE_DOC_UPLOAD; // {stage} doc upload

        const assemblyFields = {
          tenantId: options.tenantId,
          userId: options.userId,
          accessToken: options.tokens.accessToken,
          identityToken: options.tokens.idToken,
        };

        // we have an update
        if (options.itemId) {
          assemblyFields.itemId = options.itemId;
        }

        // file meta
        const meta = file.meta;
        assemblyFields.meta = JSON.stringify(meta);

        if (file.type.startsWith("application")) {
          templateId = process.env.REACT_APP_TEMPLATE_DOC_UPLOAD; // {stage}_doc_upload
        }
        if (file.type.startsWith("application/pdf")) {
          templateId = process.env.REACT_APP_TEMPLATE_PDF_CREATE_THUMBNAIL; // {stage}_pdf_create_thumbnail
        }
        if (file.type.startsWith("video")) {
          templateId = process.env.REACT_APP_TEMPLATE_VIDEO_CREATE_THUMBNAIL; // {stage}_video_create_thumbnail
        }
        if (file.type.startsWith("image")) {
          templateId = process.env.REACT_APP_TEMPLATE_IMAGE_CREATE_THUMBNAIL; // {stage}_image_create_thumbnail
        }
        if (file.type.startsWith("text/html")) {
          templateId = process.env.REACT_APP_TEMPLATE_LINK_CREATE_THUMBNAIL; // {stage}_link_create_thumbnail
          // set the original link source
          assemblyFields.source = file.remote.body.url || file.remote.body.fileId;
        }

        // add ownerId and collectionId if this is a collection addition
        addCollectionFieldsIfNecessary(assemblyFields, options.collectionState);

        return {
          params: {
            auth: { key: process.env.REACT_APP_TRANSLOADIT_AUTH_KEY },
            template_id: templateId,
            notify_url: NOTIFY_URL,
          },
          fields: assemblyFields,
        };
      },
    });

  // configure Uppy event handling
  uppy.on("file-removed", (file) => {});

  uppy.on("file-added", (file) => {
    // console.log({ file, info: "uppy.on file-added" });
    if (file.name === "noname") {
      uppy.removeFile(file.id);

      if (file.remote && file.remote.body.url) {
        let fileUrl = file.remote.body.url;
        const lastCharacter = fileUrl.charAt(fileUrl.length - 1);

        if (lastCharacter === "/") {
          fileUrl = fileUrl.slice(0, fileUrl.length - 1);
          uppy.getPlugin("Url").addFile(fileUrl);
        }
        uppy.setFileMeta(file.id, { name: fileUrl });
      }
    }

    if (file.type.includes("text/html")) {
      options.items.forEach((item) => {
        if (item.ext === "html" && item.urls.source === file.remote.body.url) {
          const pastDays = moment().diff(moment.parseZone(item.updatedAt), "days");

          uppy.info(
            {
              message: `${file.meta.name} is already exist!`,
              details: `An item ${file.meta.name} was updated ${pastDays} days ago`,
            },
            "warning",
            5000
          );

          return;
        }
      });
    } else {
      options.items.forEach((item) => {
        if (item.ext !== "html" && item.name === file.name) {
          const pastDays = moment().diff(moment.parseZone(item.updatedAt), "days");

          uppy.info(
            {
              message: `${file.meta.name} is already exist!`,
              details: `An item ${file.meta.name} was updated ${pastDays} days ago`,
            },
            "warning",
            5000
          );

          return;
        }
      });
    }
  });

  // handle the case where the title is updated
  // we'll still store the original name in the meta
  uppy.on("dashboard:file-edit-start", (file) => {
    // console.log({ file, info: "uppy.on dashboard:file-edit-start" });
    const meta = file ? file.meta : null;
    if (meta) {
      if (meta.isShared) {
        options.isShared = meta.isShared;
      }
      if (meta.updateThumbnail) {
        options.updateThumbnail = meta.updateThumbnail;
      }
    }
  });
  // handle the case where the title is updated
  // we'll still store the original name in the meta
  uppy.on("dashboard:file-edit-complete", (file) => {
    const meta = file ? file.meta : null;
    if (meta) {
      if (meta.contentTitle && meta.contentTitle !== meta.name) {
        uppy.setFileMeta(file.id, {
          name: meta.contentTitle, // Update the file name to the new contentTitle
        });
      }
    }
  });

  uppy.on('upload', (data) => {
    // console.log({ info: "uppy.on upload", data, options });
    const urlFiles = options.getUrlFiles();
    if (urlFiles && urlFiles.length > 0) {
      // console.log({ urlFiles, info: "uppy.on upload" });
      uppy.info("Importing URLs...", "info", 5000);
    }
  });

  uppy.on("complete", async (result) => {
    // console.log({info: "uppy.on complete", result});
    await importUrlFiles(options);
    
    // console.log("uppy.on compled import");
    addEventNameToEndpoint(options.Analytics, `${_.get(options.cognitoUser, "attributes[custom:role]", "")}-item-add`);

    options.Analytics.record({
      name: `${_.get(options.cognitoUser, "attributes[custom:role]", "")}-item-add`,
      attributes: {
        userId: _.get(options.cognitoUser, "username", ""),
        itemCount: result.successful.length.toString(),
        path: options.path,
      },
    });

    if (result.failed.length === 0) {
      toast.success("Uploaded - now processing... ", {
        position: toast.POSITION.TOP_RIGHT,
      });

      options.onClose();
    }
  });
  return uppy;
};